import { UNITS_BY_ID, LAND_UNITS, type LandUnit } from "@/data/bdLandUnits";

/** Convert `amount` of unit `from` into unit `to` via sq ft as the base. */
export function convertLand(amount: number, fromId: string, toId: string): number {
  const from = UNITS_BY_ID[fromId];
  const to = UNITS_BY_ID[toId];
  if (!from || !to || !Number.isFinite(amount)) return NaN;
  const sqft = amount * from.sqft;
  return sqft / to.sqft;
}

export function toSqft(amount: number, fromId: string): number {
  const u = UNITS_BY_ID[fromId];
  if (!u || !Number.isFinite(amount)) return NaN;
  return amount * u.sqft;
}

export function fromSqft(sqft: number, toId: string): number {
  const u = UNITS_BY_ID[toId];
  if (!u || !Number.isFinite(sqft)) return NaN;
  return sqft / u.sqft;
}

/** Round to a sensible number of decimals based on magnitude. */
export function smartRound(n: number): number {
  if (!Number.isFinite(n)) return NaN;
  const abs = Math.abs(n);
  if (abs === 0) return 0;
  if (abs >= 10000) return Math.round(n);
  if (abs >= 100) return Math.round(n * 100) / 100;
  if (abs >= 1) return Math.round(n * 1000) / 1000;
  if (abs >= 0.001) return Math.round(n * 100000) / 100000;
  return Number(n.toPrecision(4));
}

/** Format with thousand separators (English grouping) + smart rounding. */
export function formatLandNumber(n: number): string {
  if (!Number.isFinite(n)) return "—";
  const r = smartRound(n);
  // Preserve trailing decimals for small fractions
  if (Math.abs(r) < 1) return r.toString();
  const [intPart, decPart] = r.toString().split(".");
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decPart ? `${grouped}.${decPart}` : grouped;
}

/** Convert an amount into ALL defined units. */
export function convertToAll(amount: number, fromId: string): { unit: LandUnit; value: number }[] {
  const sqft = toSqft(amount, fromId);
  return LAND_UNITS.map((unit) => ({ unit, value: sqft / unit.sqft }));
}

// ===== Multi-plot =====

export interface Plot {
  id: string;
  amount: number;
  unitId: string;
  label?: string;
}

export interface MultiPlotResult {
  plots: { plot: Plot; sqft: number }[];
  totalSqft: number;
  totalIn: (unitId: string) => number;
}

export function summarizePlots(plots: Plot[]): MultiPlotResult {
  const rows = plots.map((p) => ({ plot: p, sqft: toSqft(p.amount, p.unitId) }));
  const total = rows.reduce((s, r) => s + (Number.isFinite(r.sqft) ? r.sqft : 0), 0);
  return {
    plots: rows,
    totalSqft: total,
    totalIn: (unitId: string) => fromSqft(total, unitId),
  };
}

// ===== Plot area calculator =====

export type Shape = "rectangle" | "triangle" | "trapezium" | "irregular";
export type LengthUnit = "ft" | "m" | "hath";

const TO_FT: Record<LengthUnit, number> = {
  ft: 1,
  m: 3.28084,
  hath: 1.5, // 1 hath ≈ 1.5 ft
};

export function lengthToFt(value: number, unit: LengthUnit): number {
  return value * TO_FT[unit];
}

export interface AreaInputs {
  shape: Shape;
  unit: LengthUnit;
  // rectangle
  length?: number;
  width?: number;
  // triangle
  base?: number;
  height?: number;
  side1?: number;
  side2?: number;
  side3?: number;
  // trapezium
  parallelA?: number;
  parallelB?: number;
  // irregular 4-sided (sides + diagonal split into two triangles)
  s1?: number;
  s2?: number;
  s3?: number;
  s4?: number;
  diagonal?: number;
}

/** Heron's formula for triangle area given three sides (in ft). */
function heron(a: number, b: number, c: number): number {
  const s = (a + b + c) / 2;
  const inside = s * (s - a) * (s - b) * (s - c);
  return inside > 0 ? Math.sqrt(inside) : NaN;
}

/** Returns plot area in SQUARE FEET (or NaN if inputs invalid). */
export function plotAreaSqft(i: AreaInputs): number {
  const k = TO_FT[i.unit];
  switch (i.shape) {
    case "rectangle": {
      if (!i.length || !i.width) return NaN;
      return i.length * i.width * k * k;
    }
    case "triangle": {
      // Prefer base × height when given
      if (i.base && i.height) return 0.5 * i.base * i.height * k * k;
      if (i.side1 && i.side2 && i.side3) {
        return heron(i.side1 * k, i.side2 * k, i.side3 * k);
      }
      return NaN;
    }
    case "trapezium": {
      if (!i.parallelA || !i.parallelB || !i.height) return NaN;
      return 0.5 * (i.parallelA + i.parallelB) * i.height * k * k;
    }
    case "irregular": {
      // Split quadrilateral into two triangles via diagonal d:
      //   triangle 1: s1, s2, d
      //   triangle 2: s3, s4, d
      if (!i.s1 || !i.s2 || !i.s3 || !i.s4 || !i.diagonal) return NaN;
      const d = i.diagonal * k;
      const t1 = heron(i.s1 * k, i.s2 * k, d);
      const t2 = heron(i.s3 * k, i.s4 * k, d);
      if (!Number.isFinite(t1) || !Number.isFinite(t2)) return NaN;
      return t1 + t2;
    }
  }
}

// ===== Price estimator =====

export interface PriceInputs {
  area: number;
  areaUnitId: string;
  pricePerUnit: number;
  priceUnitId: string; // unit the price is quoted per
}

export interface PriceResult {
  totalPrice: number;
  pricePerSqft: number;
  areaSqft: number;
  areaInPriceUnit: number;
}

export function estimatePrice(p: PriceInputs): PriceResult {
  const areaSqft = toSqft(p.area, p.areaUnitId);
  const priceUnit = UNITS_BY_ID[p.priceUnitId];
  const pricePerSqft = priceUnit ? p.pricePerUnit / priceUnit.sqft : NaN;
  const areaInPriceUnit = areaSqft / (priceUnit?.sqft ?? 1);
  return {
    totalPrice: areaInPriceUnit * p.pricePerUnit,
    pricePerSqft,
    areaSqft,
    areaInPriceUnit,
  };
}

/** Bangladesh registration cost rough estimator (general reference only). */
export interface RegCostInputs {
  totalPrice: number;
  /** Optional overrides — defaults reflect typical urban land registration. */
  gainTaxPct?: number;   // default 4%
  stampDutyPct?: number; // default 1.5%
  vatPct?: number;       // default 3%
  regFeePct?: number;    // default 1%
  localGovtPct?: number; // default 3% (city corporation areas)
}

export interface RegCostResult {
  gainTax: number;
  stampDuty: number;
  vat: number;
  regFee: number;
  localGovt: number;
  total: number;
  pctOfPrice: number;
}

export function estimateRegistrationCost(i: RegCostInputs): RegCostResult {
  const p = i.totalPrice;
  const gainTax = (p * (i.gainTaxPct ?? 4)) / 100;
  const stampDuty = (p * (i.stampDutyPct ?? 1.5)) / 100;
  const vat = (p * (i.vatPct ?? 3)) / 100;
  const regFee = (p * (i.regFeePct ?? 1)) / 100;
  const localGovt = (p * (i.localGovtPct ?? 3)) / 100;
  const total = gainTax + stampDuty + vat + regFee + localGovt;
  return {
    gainTax,
    stampDuty,
    vat,
    regFee,
    localGovt,
    total,
    pctOfPrice: p > 0 ? (total / p) * 100 : 0,
  };
}
